import type { MediaStreamType } from "./recordingToolSession";

export const getMediaStream = async (
    type: MediaStreamType,
    constraints?: MediaStreamConstraints,
): Promise<MediaStream> => {
    if (type === "screen") {
        return navigator.mediaDevices.getDisplayMedia({
            ...constraints,
            // @ts-ignore
            selfBrowserSurface: "include"
        });
    }
    else {
        return navigator.mediaDevices.getUserMedia(constraints);
    }
};

export const fetchDevices = async (): Promise<{cameras: MediaDeviceInfo[], mics: MediaDeviceInfo[]}> => {
    const devices = await navigator.mediaDevices.enumerateDevices();

    if (devices?.length > 0) {
        const groupedDevices = new Map<string, { mic?: MediaDeviceInfo; camera?: MediaDeviceInfo }>();

        devices.forEach(device => {
            if (device.kind !== "audioinput" && device.kind !== "videoinput") {
                return;
            }

            const key = device.groupId || device.deviceId; // Use groupId if available
            const cleanedDevice: MediaDeviceInfo = {
                deviceId: device.deviceId,
                groupId: device.groupId,
                kind: device.kind,
                label: device.label,
                toJSON: device.toJSON // Preserve method for consistency
            };

            if (!groupedDevices.has(key)) {
                groupedDevices.set(key, {});
            }

            if (device.kind === "audioinput") {
                    groupedDevices.get(key)!.mic = cleanedDevice;
            }
            else if (device.kind === "videoinput") {
                    groupedDevices.get(key)!.camera = cleanedDevice;
            }
        });

        const newCameras = Array.from(groupedDevices.values())
            .map(({ camera }) => camera)
            .filter(Boolean) as MediaDeviceInfo[];

        const newMics = Array.from(groupedDevices.values())
            .map(({ mic }) => mic)
            .filter(Boolean) as MediaDeviceInfo[];

        return { cameras: newCameras, mics: newMics };
    }
    else {
        return { cameras: [], mics: [] };
    }
};

export const permissionsDeniedHandler = (permissionDeniedCallback: (type: "Camera" | "Microphone") => void) => {
    // @ts-ignore - navigator.permissions camera is not yet in the types
    navigator.permissions.query({ name: "camera" }).then((result) => {
        result.onchange = () => {
            if (result.state === "denied") {
                permissionDeniedCallback("Camera");
            }
        };
    });
    // @ts-ignore - navigator.permissions camera is not yet in the types
    navigator.permissions.query({ name: "microphone" }).then((result) => {
        result.onchange = () => {
            if (result.state === "denied") {
                permissionDeniedCallback("Microphone");
            }
        };
    });
};

export const hasPermission = async (name: "camera" | "microphone"): Promise<boolean> => {
    try {
        // @ts-ignore - navigator.permissions camera is not yet in the types
        const { state } = await navigator.permissions.query({ name });
        return state === "granted";
    }
    catch {
        return false;
    }
};

export const getCameraWindowSize = () => {
    const halfScreenWidth = Math.floor(window.innerWidth / 2);
    const halfScreenHeight = Math.floor(window.innerHeight / 2);

    // Ensure even width and height for encoding
    const width = halfScreenWidth % 2 === 0 ? halfScreenWidth : halfScreenWidth - 1;
    const height = halfScreenHeight % 2 === 0 ? halfScreenHeight : halfScreenHeight - 1;
    return { width, height };
};
